/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * @fileoverview Voice diagnostics dev panel component
 * @version 2.0.0
 * @since v0.0.07
 *
 * Purpose: Dev/ops panel (behind a flag) showing current voice mode,
 * buffer profile, RTT, packet loss, and active pathway (STS vs STT→LLM→TTS).
 *
 * Per FRONTEND_TODO.MD:
 * - Subscribe to diagnostic WS events
 * - Render metrics
 * - Add manual toggle for pathway selection
 *
 * Enhanced: December 16, 2025 (Agent A - Voice Pipeline)
 * - PRD 4: Dev-only visibility control
 * - Only visible when ?debug=voice query param OR localStorage.voiceDebug === 'true'
 * - Never visible in production without explicit flag
 * - Shows preset locked indicator when adaptive buffering is disabled
 *
 * Dependencies:
 * - websocketService for diagnostic events
 * - AudioProcessor for capture stats
 * - VoicePlaybackService for playback stats
 * - audioSettings for configuration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { webSocketService } from '../../services/websocket/websocketService';
import { audioProcessor } from '../../integration/AudioProcessor';
import { voicePlaybackService, type PlaybackStats, type BufferPreset } from '../../services/voice';
import {
  VOICE_BRIDGE_CONFIG,
  getAllJitterBufferPresets,
  type VoiceConnectionState,
  type JitterBufferPresetId
} from '../../config/audioSettings';
import { type VoiceMode, VOICE_MODES } from './VoiceControls';
import { voiceDiagnostics, formatMessage } from '../../i18n/voiceMessages';

/**
 * Voice pathway types
 * STS: Speech-to-Speech (real-time, low latency)
 * STT_LLM_TTS: Speech-to-Text → LLM → Text-to-Speech (traditional, more flexible)
 */
export type VoicePathway = 'sts' | 'stt_llm_tts' | 'auto';

/**
 * Diagnostic data structure
 */
export interface VoiceDiagnostics {
  // Connection metrics
  connectionState: VoiceConnectionState;
  wsReadyState: number | undefined;
  latencyMs: number;
  rttMs: number;
  packetLoss: number;

  // Audio capture
  captureActive: boolean;
  chunksSent: number;
  bufferedSamples: number;
  sampleRate: number;

  // Audio playback
  playbackActive: boolean;
  playbackStats: PlaybackStats;
  bufferPreset: BufferPreset;

  // Voice mode
  voiceMode: VoiceMode;
  pathway: VoicePathway;

  // Session info
  sessionId: string;
  participantId: string;

  // Timestamps
  lastUpdate: number;
  connectionDuration: number;
}

export interface VoiceDiagnosticsProps {
  /** Current connection state */
  connectionState: VoiceConnectionState;
  /** Current voice mode */
  voiceMode: VoiceMode;
  /** Session ID */
  sessionId: string;
  /** Participant ID */
  participantId: string;
  /** Enable pathway toggle (default: false) */
  enablePathwayToggle?: boolean;
  /** Callback when pathway is manually changed */
  onPathwayChange?: (pathway: VoicePathway) => void;
  /** Optional CSS class name */
  className?: string;
  /** Collapsed by default */
  defaultCollapsed?: boolean;
}

/**
 * Check if voice diagnostics should be visible
 * 
 * PRD 4: Only visible when:
 * - ?debug=voice query param is present, OR
 * - localStorage.getItem('voiceDebug') === 'true'
 * 
 * Never visible in production without explicit flag.
 */
function shouldShowDiagnostics(): boolean {
  // Check URL query param
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'voice') {
      return true;
    }
    
    // Check localStorage flag
    try {
      if (localStorage.getItem('voiceDebug') === 'true') {
        return true;
      }
    } catch {
      // localStorage not available (SSR, private mode, etc.)
    }
  }
  return false;
}

/**
 * VoiceDiagnostics Component
 *
 * Developer/operator panel for monitoring voice pipeline health.
 * Shows real-time metrics including:
 * - Connection state and latency
 * - Audio capture statistics
 * - Playback buffer status
 * - Packet loss and jitter
 * - Active voice pathway
 *
 * PRD 4: Hidden unless ?debug=voice or localStorage.voiceDebug='true'
 * Never visible in production without explicit flag.
 */
export const VoiceDiagnosticsPanel: React.FC<VoiceDiagnosticsProps> = ({
  connectionState,
  voiceMode,
  sessionId,
  participantId,
  enablePathwayToggle = false,
  onPathwayChange,
  className = '',
  defaultCollapsed = true
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [diagnostics, setDiagnostics] = useState<Partial<VoiceDiagnostics>>({});
  const [pathway, setPathway] = useState<VoicePathway>('auto');
  const [connectionStartTime] = useState(Date.now());
  const [presetLocked, setPresetLocked] = useState(false);
  
  /**
   * Check visibility on mount (PRD 4)
   */
  useEffect(() => {
    setIsVisible(shouldShowDiagnostics());
  }, []);

  /**
   * Update diagnostics at regular interval
   * Only runs if panel is visible (PRD 4 optimization)
   */
  useEffect(() => {
    if (!isVisible) return;
    
    const updateDiagnostics = () => {
      const captureStats = audioProcessor.getStats();
      const playbackStats = voicePlaybackService.getStats();
      const wsState = webSocketService.getReadyState();
      
      // Check if preset is locked (for indicator display)
      const locked = voicePlaybackService.isPresetLocked?.() ?? false;
      setPresetLocked(locked);

      setDiagnostics({
        connectionState,
        wsReadyState: wsState,
        latencyMs: playbackStats.averageLatencyMs,
        rttMs: playbackStats.averageLatencyMs * 2, // Approximate
        packetLoss: playbackStats.packetsReceived > 0
          ? (playbackStats.packetsDropped / playbackStats.packetsReceived) * 100
          : 0,
        captureActive: captureStats.isCapturing,
        chunksSent: captureStats.chunksSent,
        bufferedSamples: captureStats.bufferedSamples,
        sampleRate: captureStats.sampleRate,
        playbackActive: voicePlaybackService.isActive(),
        playbackStats,
        bufferPreset: voicePlaybackService.getBufferPreset() as BufferPreset,
        voiceMode,
        pathway,
        sessionId,
        participantId,
        lastUpdate: Date.now(),
        connectionDuration: Date.now() - connectionStartTime
      });
    };

    // Initial update
    updateDiagnostics();

    // Update every second
    const interval = setInterval(updateDiagnostics, 1000);

    return () => clearInterval(interval);
  }, [isVisible, connectionState, voiceMode, sessionId, participantId, pathway, connectionStartTime]);

  /**
   * Subscribe to diagnostic WebSocket events
   */
  useEffect(() => {
    const handleDiagnosticEvent = (data: {
      rttMs?: number;
      serverLoad?: number;
      activePathway?: VoicePathway;
    }) => {
      setDiagnostics((prev) => ({
        ...prev,
        rttMs: data.rttMs ?? prev.rttMs,
        pathway: data.activePathway ?? prev.pathway
      }));
    };

    webSocketService.subscribe('voice.diagnostics', handleDiagnosticEvent);

    return () => {
      webSocketService.unsubscribe('voice.diagnostics', handleDiagnosticEvent);
    };
  }, []);

  /**
   * Handle pathway change
   */
  const handlePathwayChange = (newPathway: VoicePathway) => {
    setPathway(newPathway);
    onPathwayChange?.(newPathway);

    // Send to backend
    webSocketService.send('voice.pathway_preference', {
      pathway: newPathway,
      sessionId,
      participantId
    });
  };

  /**
   * Format duration
   */
  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  /**
   * Get status color
   */
  const getStatusColor = (value: number, thresholds: { good: number; warn: number }): string => {
    if (value <= thresholds.good) return '#22c55e';
    if (value <= thresholds.warn) return '#eab308';
    return '#ef4444';
  };

  /**
   * Get WebSocket state name
   */
  const getWsStateName = (state: number | undefined): string => {
    switch (state) {
      case 0: return 'CONNECTING';
      case 1: return 'OPEN';
      case 2: return 'CLOSING';
      case 3: return 'CLOSED';
      default: return 'UNKNOWN';
    }
  };

  const stats = diagnostics.playbackStats;
  const presets = getAllJitterBufferPresets();

  // PRD 4: Return null if not visible (dev-only component)
  if (!isVisible) {
    return null;
  }

  return (
    <div className={`voice-diagnostics ${className}`} style={styles.container} role="region" aria-label={voiceDiagnostics.header.ariaLabel}>
      {/* Header */}
      <div
        style={styles.header}
        onClick={() => setIsCollapsed(!isCollapsed)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsCollapsed(!isCollapsed);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={!isCollapsed}
        aria-controls="voice-diagnostics-content"
        aria-label={voiceDiagnostics.header.ariaLabel}
      >
        <span style={styles.headerIcon} aria-hidden="true">🔧</span>
        <span style={styles.headerTitle}>{voiceDiagnostics.header.title}</span>
        <span style={styles.headerToggle} aria-hidden="true">{isCollapsed ? '▼' : '▲'}</span>
      </div>

      {!isCollapsed && (
        <div id="voice-diagnostics-content" style={styles.content} aria-live="polite" aria-atomic="false">
          {/* Connection Section */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{voiceDiagnostics.sections.connection}</div>
            <div style={styles.metricsGrid}>
              <MetricItem
                label={voiceDiagnostics.labels.state}
                value={connectionState}
                color={connectionState === 'connected' ? '#22c55e' : '#ef4444'}
              />
              <MetricItem
                label={voiceDiagnostics.labels.webSocket}
                value={getWsStateName(diagnostics.wsReadyState)}
                color={diagnostics.wsReadyState === 1 ? '#22c55e' : '#6b7280'}
              />
              <MetricItem
                label={voiceDiagnostics.labels.duration}
                value={formatDuration(diagnostics.connectionDuration ?? 0)}
              />
              <MetricItem
                label={voiceDiagnostics.labels.rtt}
                value={`${Math.round(diagnostics.rttMs ?? 0)}ms`}
                color={getStatusColor(diagnostics.rttMs ?? 0, { good: 100, warn: 300 })}
                tooltip={voiceDiagnostics.tooltips.rtt}
              />
            </div>
          </div>

          {/* Audio Capture Section */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{voiceDiagnostics.sections.capture}</div>
            <div style={styles.metricsGrid}>
              <MetricItem
                label={voiceDiagnostics.labels.active}
                value={diagnostics.captureActive ? voiceDiagnostics.common.yes : voiceDiagnostics.common.no}
                color={diagnostics.captureActive ? '#22c55e' : '#6b7280'}
              />
              <MetricItem
                label={voiceDiagnostics.labels.chunksSent}
                value={String(diagnostics.chunksSent ?? 0)}
              />
              <MetricItem
                label={voiceDiagnostics.labels.buffer}
                value={`${diagnostics.bufferedSamples ?? 0} samples`}
              />
              <MetricItem
                label={voiceDiagnostics.labels.sampleRate}
                value={`${(diagnostics.sampleRate ?? 48000) / 1000}kHz`}
              />
            </div>
          </div>

          {/* Playback Section */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{voiceDiagnostics.sections.playback}</div>
            <div style={styles.metricsGrid}>
              <MetricItem
                label={voiceDiagnostics.labels.active}
                value={diagnostics.playbackActive ? voiceDiagnostics.common.yes : voiceDiagnostics.common.no}
                color={diagnostics.playbackActive ? '#22c55e' : '#6b7280'}
              />
              <MetricItem
                label={voiceDiagnostics.labels.preset}
                value={`${diagnostics.bufferPreset ?? 'balanced'}${presetLocked ? ' 🔒' : ''}`}
                color={presetLocked ? '#f59e0b' : undefined}
                tooltip={presetLocked ? voiceDiagnostics.tooltips.presetLocked : undefined}
              />
              <MetricItem
                label={voiceDiagnostics.labels.buffer}
                value={`${Math.round(stats?.currentBufferMs ?? 0)}ms`}
                color={getStatusColor(stats?.bufferUnderruns ?? 0, { good: 0, warn: 5 })}
              />
              <MetricItem
                label={voiceDiagnostics.labels.latency}
                value={`${Math.round(stats?.averageLatencyMs ?? 0)}ms`}
                color={getStatusColor(stats?.averageLatencyMs ?? 0, { good: 100, warn: 250 })}
                tooltip={voiceDiagnostics.tooltips.latency}
              />
            </div>
            <div style={styles.metricsGrid}>
              <MetricItem
                label={voiceDiagnostics.labels.received}
                value={String(stats?.packetsReceived ?? 0)}
              />
              <MetricItem
                label={voiceDiagnostics.labels.played}
                value={String(stats?.packetsPlayed ?? 0)}
              />
              <MetricItem
                label={voiceDiagnostics.labels.dropped}
                value={String(stats?.packetsDropped ?? 0)}
                color={getStatusColor(stats?.packetsDropped ?? 0, { good: 0, warn: 10 })}
              />
              <MetricItem
                label={voiceDiagnostics.labels.jitter}
                value={`${Math.round(stats?.jitterMs ?? 0)}ms`}
                color={getStatusColor(stats?.jitterMs ?? 0, { good: 20, warn: 50 })}
                tooltip={voiceDiagnostics.tooltips.jitter}
              />
            </div>
          </div>

          {/* Quality Section */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{voiceDiagnostics.sections.quality}</div>
            <div style={styles.metricsGrid}>
              <MetricItem
                label={voiceDiagnostics.labels.packetLoss}
                value={`${(diagnostics.packetLoss ?? 0).toFixed(1)}%`}
                color={getStatusColor(diagnostics.packetLoss ?? 0, { good: 1, warn: 5 })}
                tooltip={voiceDiagnostics.tooltips.packetLoss}
              />
              <MetricItem
                label={voiceDiagnostics.labels.underruns}
                value={String(stats?.bufferUnderruns ?? 0)}
                color={getStatusColor(stats?.bufferUnderruns ?? 0, { good: 0, warn: 5 })}
              />
              <MetricItem
                label={voiceDiagnostics.labels.overruns}
                value={String(stats?.bufferOverruns ?? 0)}
                color={getStatusColor(stats?.bufferOverruns ?? 0, { good: 0, warn: 5 })}
              />
            </div>
          </div>

          {/* Mode Section */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{voiceDiagnostics.sections.mode}</div>
            <div style={styles.metricsGrid}>
              <MetricItem
                label={voiceDiagnostics.labels.mode}
                value={VOICE_MODES[voiceMode].label}
              />
              <MetricItem
                label={voiceDiagnostics.labels.pathway}
                value={pathway === 'sts' ? voiceDiagnostics.pathway.sts : pathway === 'stt_llm_tts' ? voiceDiagnostics.pathway.sttLlmTts : voiceDiagnostics.pathway.auto}
              />
            </div>

            {/* Pathway Toggle (if enabled) */}
            {enablePathwayToggle && (
              <div style={styles.pathwaySelector}>
                <label style={styles.pathwayLabel}>{voiceDiagnostics.labels.forcePathway}</label>
                <div style={styles.pathwayButtons}>
                  {(['auto', 'sts', 'stt_llm_tts'] as VoicePathway[]).map((p) => (
                    <button
                      key={p}
                      onClick={() => handlePathwayChange(p)}
                      style={{
                        ...styles.pathwayButton,
                        ...(pathway === p ? styles.pathwayButtonActive : {})
                      }}
                      aria-label={p === 'auto' ? voiceDiagnostics.pathway.ariaLabelAuto : p === 'sts' ? voiceDiagnostics.pathway.ariaLabelSts : voiceDiagnostics.pathway.ariaLabelSttLlmTts}
                      aria-pressed={pathway === p}
                    >
                      {p === 'auto' ? voiceDiagnostics.pathway.auto : p === 'sts' ? voiceDiagnostics.pathway.sts : voiceDiagnostics.pathway.sttLlmTts}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Buffer Preset Selector */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{voiceDiagnostics.sections.buffer}</div>
            <div style={styles.presetSelector}>
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => voicePlaybackService.setBufferPreset(preset.id as BufferPreset)}
                  style={{
                    ...styles.presetButton,
                    ...(diagnostics.bufferPreset === preset.id ? styles.presetButtonActive : {})
                  }}
                  aria-label={formatMessage(voiceDiagnostics.tooltips.setBufferPreset, { label: preset.label, description: preset.description })}
                  aria-pressed={diagnostics.bufferPreset === preset.id}
                  title={preset.description}
                >
                  {preset.label} ({preset.bufferMs}ms)
                </button>
              ))}
            </div>
          </div>

          {/* Session Info */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{voiceDiagnostics.sections.session}</div>
            <div style={styles.sessionInfo}>
              <div style={styles.sessionRow}>
                <span style={styles.sessionLabel}>{voiceDiagnostics.labels.session}</span>
                <code style={styles.sessionValue}>{sessionId || voiceDiagnostics.common.notAvailable}</code>
              </div>
              <div style={styles.sessionRow}>
                <span style={styles.sessionLabel}>{voiceDiagnostics.labels.participant}</span>
                <code style={styles.sessionValue}>{participantId || voiceDiagnostics.common.notAvailable}</code>
              </div>
            </div>
          </div>

          {/* Last Update */}
          <div style={styles.footer}>
            {voiceDiagnostics.labels.lastUpdate}: {new Date(diagnostics.lastUpdate ?? 0).toLocaleTimeString()}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Metric item sub-component
 */
interface MetricItemProps {
  label: string;
  value: string;
  color?: string;
  tooltip?: string;
}

const MetricItem: React.FC<MetricItemProps> = ({ label, value, color, tooltip }) => (
  <div style={styles.metricItem} aria-label={`${label}: ${value}`}>
    <span style={styles.metricLabel} title={tooltip}>{label}</span>
    <span style={{ ...styles.metricValue, color: color ?? 'rgba(255, 255, 255, 0.9)' }} title={tooltip} aria-hidden="true">
      {value}
    </span>
  </div>
);

/**
 * Component styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '12px',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    cursor: 'pointer',
    userSelect: 'none'
  },
  headerIcon: {
    fontSize: '16px'
  },
  headerTitle: {
    flex: 1,
    fontWeight: '600',
    color: '#a78bfa'
  },
  headerToggle: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '10px'
  },
  content: {
    padding: '12px'
  },
  section: {
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
  },
  sectionTitle: {
    fontSize: '10px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: 'rgba(255, 255, 255, 0.5)',
    marginBottom: '8px'
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
    marginBottom: '8px'
  },
  metricItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  metricLabel: {
    fontSize: '10px',
    color: 'rgba(255, 255, 255, 0.4)'
  },
  metricValue: {
    fontSize: '12px',
    fontWeight: '500'
  },
  pathwaySelector: {
    marginTop: '8px'
  },
  pathwayLabel: {
    fontSize: '10px',
    color: 'rgba(255, 255, 255, 0.5)',
    marginBottom: '6px',
    display: 'block'
  },
  pathwayButtons: {
    display: 'flex',
    gap: '4px'
  },
  pathwayButton: {
    padding: '4px 8px',
    fontSize: '10px',
    borderRadius: '4px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer'
  },
  pathwayButtonActive: {
    backgroundColor: 'rgba(139, 92, 246, 0.3)',
    borderColor: '#8b5cf6',
    color: '#fff'
  },
  presetSelector: {
    display: 'flex',
    gap: '4px'
  },
  presetButton: {
    flex: 1,
    padding: '6px 8px',
    fontSize: '10px',
    borderRadius: '4px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    textAlign: 'center'
  },
  presetButtonActive: {
    backgroundColor: 'rgba(34, 197, 94, 0.3)',
    borderColor: '#22c55e',
    color: '#fff'
  },
  sessionInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  sessionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  sessionLabel: {
    fontSize: '10px',
    color: 'rgba(255, 255, 255, 0.4)',
    minWidth: '70px'
  },
  sessionValue: {
    fontSize: '10px',
    color: 'rgba(255, 255, 255, 0.7)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    padding: '2px 6px',
    borderRadius: '3px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  footer: {
    fontSize: '9px',
    color: 'rgba(255, 255, 255, 0.3)',
    textAlign: 'center',
    paddingTop: '8px'
  }
};

export default VoiceDiagnosticsPanel;

